import { useEffect, useMemo, useState } from "react";
import MultiSelector from "../../../../../components/Selectors/MultiSelector";
import {
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Group,
  HoverCard,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  IconArchive,
  IconCircleFilled,
  IconExternalLink,
  IconListDetails,
  IconMessageCircle,
} from "@tabler/icons-react";
import { parseDate } from "../../../../../functions/dates";
import { getProgrammations } from "../../../../../api/WorkflowAPI";
import { useConfigStore } from "../../../../../stores/ConfigStore";
import { useThemeStore } from "../../../../../stores/ThemeStore";
import { IconLinkedin } from "../../../../../components/Icons/IconLinkedin";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { IconEmail } from "../../../../../assets/icons/IconEmail";
import { Link } from "react-router-dom";

function getOptionName(option) {
  return `${option.workflow_name} #${option.id}`;
}

const ListDisplay = ({ lists }) => {
  const lang = useLanguage((s) => s.language);

  return (
    <HoverCard openDelay={300} position="left" offset={2} withArrow>
      <HoverCard.Target>
        <ThemeIcon size={"xs"} variant="light" color="secondary" ml={"auto"}>
          <IconListDetails size={14} />
        </ThemeIcon>
      </HoverCard.Target>

      <HoverCard.Dropdown>
        <Stack>
          {lists.length > 0 ? (
            lists?.map((list) => (
              <Group key={list.contact_list_id} gap={"xs"}>
                <IconCircleFilled size={6} color="black" />
                <Anchor
                  size="sm"
                  target="_blank"
                  fw={600}
                  href={`/contact-lists/${list.contact_list_id}`}
                >
                  {list.name} #{list.contact_list_id}
                </Anchor>
              </Group>
            ))
          ) : (
            <Text c={"dimmed"}>{lang.prm.nurturing.no_list}</Text>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

const SentCampaignExclusion = () => {
  const lang = useLanguage((s) => s.language);
  const listStore = useConfigStore((s) => s.programmations);
  const programData = useCampaignStore((s) => s.programData);
  const setProgramData = useCampaignStore((s) => s.setProgramData);
  const [campaigns, setCampaigns] = useState(
    programData?.exclude_programmation_ids?.map((listId) => {
      let list = listStore.find((x) => x.id === listId);
      if (list) {
        return {
          ...list,
          option_name: getOptionName(list),
        };
      }
      return;
    }) || []
  );

  const [loading, setLoading] = useState(false);

  const [emailSelected, setEmailSelected] = useState(false);
  const [linkedinSelected, setLinkedinSelected] = useState(false);
  const [smsSelected, setSmsSelected] = useState(false);

  const options = useMemo(() => {
    return listStore
      .filter(x => x.id !== programData?.id)
      .sort((a, b) => Date.parse(b.created_on) - Date.parse(a.created_on))
      .map((list) => ({
        ...list,
        option_name: getOptionName(list),
      })).sort((a, b) => programData.exclude_programmation_ids?.includes(b.id) - programData.exclude_programmation_ids?.includes(a.id));
  }, [listStore]);

  const handleSelectAll = () => {
    if (programData.exclude_programmation_ids.length === options.length) {
      setProgramData({
        ...programData,
        exclude_programmation_ids: [],
      });
      setEmailSelected(false);
      setLinkedinSelected(false);
      setSmsSelected(false);
    } else {
      setProgramData({
        ...programData,
        exclude_programmation_ids: options.map((x) => x.id),
      });
      setEmailSelected(true);
      setLinkedinSelected(true);
      setSmsSelected(true);
    }
  };

  const selectType = (type) => {
    if (type === "email") {
      let emailCampaigns = options
        .filter((x) => x[`has_email_step`])
        .map((x) => x.id);
      if (!emailSelected) {
        setEmailSelected(true);
        return setProgramData({
          ...programData,
          exclude_programmation_ids: [
            ...programData.exclude_programmation_ids,
            ...emailCampaigns,
          ],
        });
      } else {
        setEmailSelected(false);

        return setProgramData({
          ...programData,
          exclude_programmation_ids:
            programData.exclude_programmation_ids.filter(
              (x) => !emailCampaigns.includes(x)
            ),
        });
      }
    }
    if (type === "linkedin") {
      let linkedinCampaigns = options
        .filter(
          (x) =>
            x[`has_linkedin_invitation_step`] ||
            x[`has_linkedin_message_step`] ||
            x[`has_linkedin_visit_step`]
        )
        .map((x) => x.id);

      if (!linkedinSelected) {
        setLinkedinSelected(true);
        return setProgramData({
          ...programData,
          exclude_programmation_ids: [
            ...programData.exclude_programmation_ids,
            ...linkedinCampaigns,
          ],
        });
      } else {
        setLinkedinSelected(false);

        return setProgramData({
          ...programData,
          exclude_programmation_ids:
            programData.exclude_programmation_ids.filter(
              (x) => !linkedinCampaigns.includes(x)
            ),
        });
      }
    }
    if (type === "sms") {
      let smsCampaigns = options
        .filter((x) => x[`has_sms_step`])
        .map((x) => x.id);

      if (!smsSelected) {
        setSmsSelected(true);
        return setProgramData({
          ...programData,
          exclude_programmation_ids: [
            ...programData.exclude_programmation_ids,
            ...smsCampaigns,
          ],
        });
      } else {
        setSmsSelected(false);

        return setProgramData({
          ...programData,
          exclude_programmation_ids:
            programData.exclude_programmation_ids.filter(
              (x) => !smsCampaigns.includes(x)
            ),
        });
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    getProgrammations({
      per_page: 100,
      sort: {
        field_name: "id",
        sort_direction: "desc",
      },
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCampaigns(
      programData?.exclude_programmation_ids
        ?.map((listId) => {
          let list = listStore?.find((x) => x.id === listId);
          if (list) {
            return {
              ...list,
              option_name: getOptionName(list),
            };
          }
          return;
        })
        ?.filter(Boolean) || []
    );
  }, [listStore, programData]);

  return (
    <Stack h={"100%"} w={"50%"} gap={"xs"}>
      <Title order={5}>{lang.campaign.exclusions.sent_title}</Title>

      {/* <Group gap={6} wrap="nowrap">
        <Text size="sm">{lang.campaign.exclusions.sent_text}</Text>
      </Group> */}
      <MultiSelector
        options={options}
        loading={loading}
        values={campaigns}
        onChange={(v) =>
          setProgramData({
            ...programData,
            exclude_programmation_ids: v.map((x) => x.id),
          })
        }
        placeholder={lang.campaign.exclusions.search_campaign}
        rightOptionSection={(item) => {
          return (
            <>
              {item?.contact_lists && (
                <ListDisplay lists={item?.contact_lists || []} />
              )}
              {item?.archived && (
                <Tooltip
                  label={
                    lang.workflows.program.step_exclusion.columns.archived.true
                  }
                >
                  <IconArchive size={16} strokeWidth={1.2} color="red" />
                </Tooltip>
              )}
              <Tooltip label={"Date prog"}>
                <span style={{ fontSize: 10, whiteSpace: "nowrap" }}>
                  {parseDate(item?.date_start)}
                </span>
              </Tooltip>
              {item?.id && (
                <ActionIcon
                  component={Link}
                  target="_blank"
                  to={`/statistics/campaigns?highlight=${item.id}`}
                  size={"xs"}
                  variant="subtle"
                >
                  <IconExternalLink />
                </ActionIcon>
              )}
            </>
          );
        }}
        leftOptionSection={(item) => {
          if (item[`has_email_step`]) {
            return (
              <ThemeIcon
                color={useThemeStore.getState().actionColors.email}
                size={"sm"}
                variant="subtle"
                p={0}
                mx={-4}
              >
                <IconEmail size={16} />
              </ThemeIcon>
            );
          }
          if (item[`has_sms_step`]) {
            return (
              <ThemeIcon
                color={useThemeStore.getState().actionColors.sms}
                size={"sm"}
                variant="subtle"
                p={0}
                mx={-4}
              >
                <IconMessageCircle size={16} />
              </ThemeIcon>
            );
          }
          if (
            item[`has_linkedin_invitation_step`] ||
            item[`has_linkedin_message_step`] ||
            item[`has_linkedin_visit_step`]
          ) {
            return (
              <ThemeIcon
                color={useThemeStore.getState().actionColors.linkedin_message}
                size={"sm"}
                variant="subtle"
                p={0}
                mx={-4}
              >
                <IconLinkedin size={16} />
              </ThemeIcon>
            );
          }
        }}
        size={"md"}
        w={"100%"}
        searchable
        checkIconPosition="left"
        footer={
          <>
            <Checkbox
              label={lang.global.all_select}
              checked={
                programData.exclude_programmation_ids.length === options.length
              }
              indeterminate={
                programData.exclude_programmation_ids.length > 0 &&
                programData.exclude_programmation_ids.length < options.length
              }
              onChange={handleSelectAll}
              radius={"sm"}
            />

            <Checkbox
              label={"Emails"}
              checked={emailSelected}
              onChange={() => selectType("email")}
              radius={"sm"}
              ml={"auto"}
            />

            <Checkbox
              label={"LinkedIn"}
              checked={linkedinSelected}
              onChange={() => selectType("linkedin")}
              radius={"sm"}
            />

            <Checkbox
              label={"SMS"}
              checked={smsSelected}
              onChange={() => selectType("sms")}
              radius={"sm"}
            />
          </>
        }
      />
    </Stack>
  );
};

export default SentCampaignExclusion;
